import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';

import type { CalendarEventDto } from '@chat-emp/shared';

import { Icon } from '../components/Icon.js';
import { formatClock, formatHourLabel, formatTimeRange, formatWeekday } from '../util/format.js';
import { useIsMobile } from '../util/useIsMobile.js';
import { useNow } from '../util/useNow.js';
import {
  dayFraction,
  daysOf,
  isSameDay,
  isSameMonth,
  startOfDay,
  stepAnchor,
  type CalendarView,
} from './calendar-rules.js';
import { dayPlacement, durationMinutes, eventsOn, packLanes } from './event-rules.js';
import { formatCalendarPeriod } from './period.js';
import styles from './EventCalendar.module.css';

const VIEWS: { id: CalendarView; label: string }[] = [
  { id: 'day', label: 'Day' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
];

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

/**
 * The gutter's labels and a column's empty hour rows, built once.
 *
 * Neither depends on anything but the hour, and both would otherwise be rebuilt
 * on every render: 24 `Intl` time formats for the labels, and 168 host elements
 * for the slots across a week. Hoisted, the element identities are stable and
 * React skips the whole subtree instead of walking it.
 */
const HOUR_ROWS = HOURS.map((hour) => (
  <span key={hour} className={styles.hour}>
    {/* The label straddles the line that opens its row (see the stylesheet), so
        midnight goes unlabelled: its line is the top of the grid, where half the
        text would be cut off under the sticky head. */}
    {hour > 0 && <span className={styles.hourLabel}>{formatHourLabel(hour)}</span>}
  </span>
));

const HOUR_SLOTS = HOURS.map((hour) => <div key={hour} className={styles.slot} />);

/**
 * Where a day or week view is scrolled when it opens. The grid runs midnight to
 * midnight because interviews are not owned by office hours, but landing at
 * 00:00 shows eight hours of nothing before the first plausible slot.
 */
const OPENS_AT_HOUR = 8;

/** Events named in a month cell before the rest become a "+N". */
const MONTH_CHIPS = 2;

/** The day on screen, at the hour the grid opens on. See `onNewChatDay`. */
function chatDayStart(anchor: Date): Date {
  const start = startOfDay(anchor);
  start.setHours(OPENS_AT_HOUR);
  return start;
}

interface EventCalendarProps {
  events: CalendarEventDto[];
  /** The event the details panel is showing, so the grid can say which block. */
  openedId: string | null;
  onOpen: (event: CalendarEventDto) => void;
  /**
   * Book a Chat Day, on the day the calendar is currently showing rather than on
   * today: the button is in the header and not in a slot, so the day on screen
   * is the only thing it can mean. The hour is `OPENS_AT_HOUR`, which is where
   * the grid already scrolls to — so the draft lands where the employer is
   * looking, not at midnight.
   *
   * Optional, and its absence hides the button rather than disabling it. Booking
   * is AI-2714; until that lands there is nothing for a press to do, and the
   * feature gate's own rule is about not offering a door that does not open. A
   * disabled button, or one whose Save silently discards a filled-in form, is
   * that door with a sign on it.
   */
  onNewChatDay?: (start: Date) => void;
}

/**
 * The calendar itself — the wide half of the Event section.
 *
 * Which day is on screen and which view it wears live here rather than in
 * ChatView: nothing outside this column reads them. When the list beside it
 * starts filtering to the visible range, they lift to the page and this takes
 * them as props.
 */
export function EventCalendar({ events, openedId, onOpen, onNewChatDay }: EventCalendarProps) {
  const isMobile = useIsMobile();
  // Seven columns on a phone is seven 45px slivers, so a phone opens on the day.
  // Initial state only: switching views is the reader's call after that, and a
  // resize that silently threw away their choice would be the app arguing with
  // them.
  const [view, setView] = useState<CalendarView>(() => (isMobile ? 'day' : 'week'));
  const [anchor, setAnchor] = useState<Date>(() => startOfDay(new Date()));

  const days = useMemo(() => daysOf(view, anchor), [view, anchor]);
  const today = startOfDay(new Date());

  return (
    <section className={styles.calendar} aria-label="Event calendar">
      <header className={styles.header}>
        <div className={styles.lead}>
          {/* Today, then the pair of arrows, then what they are moving. The
              arrows step by whatever the view is — a week at a time in Week, a
              day in Day — so they read as one control with two directions and sit
              together. Flanking the label instead ties them to the thing they
              move, but their positions then depend on its width, so the right
              arrow walks around as you page. Here nothing moves. */}
          <button
            type="button"
            className={styles.today}
            onClick={() => setAnchor(startOfDay(new Date()))}
          >
            Today
          </button>

          <div className={styles.steps}>
            <button
              type="button"
              className={styles.step}
              title="Previous"
              aria-label="Previous"
              onClick={() => setAnchor((current) => stepAnchor(view, current, -1))}
            >
              <Icon name="chevronLeft" size={20} />
            </button>
            <button
              type="button"
              className={styles.step}
              title="Next"
              aria-label="Next"
              onClick={() => setAnchor((current) => stepAnchor(view, current, 1))}
            >
              <Icon name="chevronRight" size={20} />
            </button>
          </div>

          <h2 className={styles.period}>{formatCalendarPeriod(view, anchor)}</h2>
        </div>

        <div className={styles.tail}>
          <div className={styles.views}>
            {VIEWS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={option.id === view ? styles.viewActive : styles.view}
                aria-pressed={option.id === view}
                onClick={() => setView(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>

          {/*
           * The one thing on this calendar you cannot get to by pressing
           * something already on it.
           *
           * Beside the view switcher rather than in the lead: the left of this
           * header is about *navigating* — today, the arrows, where you are —
           * and this makes something. It also lands where the eye already is
           * after choosing Week.
           */}
          {onNewChatDay && (
            <button
              type="button"
              className={styles.newChatDay}
              onClick={() => onNewChatDay(chatDayStart(anchor))}
            >
              <Icon name="plus" size={16} />
              <span className={styles.newChatDayLabel}>Chat Day</span>
            </button>
          )}
        </div>
      </header>

      {view === 'month' ? (
        <MonthGrid
          days={days}
          anchor={anchor}
          today={today}
          events={events}
          openedId={openedId}
          onOpen={onOpen}
        />
      ) : (
        <TimeGrid
          key={view}
          days={days}
          today={today}
          events={events}
          openedId={openedId}
          onOpen={onOpen}
        />
      )}
    </section>
  );
}

interface GridProps {
  days: Date[];
  today: Date;
  events: CalendarEventDto[];
  /** The event the details panel is showing, so the grid can mark its block. */
  openedId: string | null;
  onOpen: (event: CalendarEventDto) => void;
}

/**
 * The month: six Monday-led rows, every cell the same height whatever the month
 * is (see WEEKS_IN_MONTH_GRID). Days from the months either side stay on the
 * grid rather than being blanked out — an interview on the 1st is easier to find
 * when the week it belongs to is whole.
 */
function MonthGrid({
  days,
  anchor,
  today,
  events,
  openedId,
  onOpen,
}: GridProps & { anchor: Date }) {
  return (
    <div className={styles.month}>
      <div className={styles.monthHead}>
        {days.slice(0, 7).map((day) => (
          <span key={day.getDay()} className={styles.weekday}>
            {formatWeekday(day)}
          </span>
        ))}
      </div>
      <div className={styles.monthBody}>
        {days.map((day) => {
          const onThisDay = eventsOn(events, day);

          return (
            <div
              key={day.toISOString()}
              className={[
                styles.cell,
                !isSameMonth(day, anchor) && styles.cellOutside,
                isWeekend(day) && styles.cellWeekend,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <span className={isSameDay(day, today) ? styles.dateToday : styles.date}>
                {day.getDate()}
              </span>

              {onThisDay.slice(0, MONTH_CHIPS).map((event) => (
                <button
                  key={event.id}
                  type="button"
                  className={[styles.chip, event.id === openedId && styles.chipOn]
                    .filter(Boolean)
                    .join(' ')}
                  title={event.title}
                  onClick={() => onOpen(event)}
                >
                  <span className={styles.chipTime}>{formatClock(event.startsAt)}</span>
                  <span className={styles.chipTitle}>{event.title}</span>
                </button>
              ))}
              {onThisDay.length > MONTH_CHIPS && (
                <span className={styles.chipMore}>+{onThisDay.length - MONTH_CHIPS} more</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Day and week are one grid with a different number of columns, so there is one
 * place that decides how tall an hour is and where the current-time line falls.
 *
 * Midnight to midnight, scrolled to the morning on open. The head is sticky so
 * the dates stay readable at 18:00, which is most of the point of scrolling a
 * calendar at all.
 */
function TimeGrid({ days, today, events, openedId, onOpen }: GridProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // The head and the body are two grids that have to line up column for column,
  // and the count is data (one day or seven), so it is set where the data is
  // rather than in a pair of near-identical stylesheet rules. The gutter width
  // still comes from the stylesheet, which is where the grid's measurements
  // live.
  const columns: CSSProperties = {
    gridTemplateColumns: `var(--cal-gutter) repeat(${days.length}, minmax(0, 1fr))`,
  };

  // Measured off the body rather than computed from a row height in TS: the
  // hour's height is a CSS number, and a copy of it here would be a second
  // source of truth that only drifts.
  useEffect(() => {
    const scroller = scrollRef.current;
    const body = bodyRef.current;
    if (!scroller || !body) return;
    scroller.scrollTop = (body.offsetHeight * OPENS_AT_HOUR) / 24;
  }, []);

  return (
    <div className={styles.time} ref={scrollRef}>
      <div className={styles.timeHead} style={columns}>
        <span className={styles.gutterHead} />
        {days.map((day) => {
          const isToday = isSameDay(day, today);
          return (
            <div key={day.toISOString()} className={styles.dayHead}>
              <span className={isToday ? styles.dayNameToday : styles.dayName}>
                {formatWeekday(day)}
              </span>
              <span className={isToday ? styles.dateToday : styles.date}>{day.getDate()}</span>
            </div>
          );
        })}
      </div>

      <div className={styles.timeBody} ref={bodyRef} style={columns}>
        <div className={styles.gutter}>{HOUR_ROWS}</div>

        {days.map((day) => (
          <div
            key={day.toISOString()}
            className={[styles.column, isWeekend(day) && styles.columnWeekend]
              .filter(Boolean)
              .join(' ')}
          >
            {HOUR_SLOTS}

            {packLanes(eventsOn(events, day)).map(({ event, lane, lanes }) => {
              const { top, height } = dayPlacement(event, day);
              // Half an hour is 28px at this scale: room for one line, which is
              // the title, with the time folded onto it beside it.
              const compact = durationMinutes(event) < 45;
              const range = formatTimeRange(event.startsAt, event.endsAt);

              return (
                <button
                  key={event.id}
                  type="button"
                  className={[
                    compact ? styles.blockCompact : styles.block,
                    event.id === openedId && styles.blockOn,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{
                    top: `${top * 100}%`,
                    height: `${height * 100}%`,
                    left: `${(lane / lanes) * 100}%`,
                    width: `${(1 / lanes) * 100}%`,
                  }}
                  title={`${event.title} · ${range}`}
                  aria-label={`${event.title}, ${range}`}
                  onClick={() => onOpen(event)}
                >
                  <span className={styles.blockTitle}>
                    <span className={styles.blockName}>{event.title}</span>
                  </span>
                  <span className={styles.blockTime}>{range}</span>
                </button>
              );
            })}

            {isSameDay(day, today) && <NowLine />}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The line across today at the current time.
 *
 * Its own component because it is the only thing on the calendar that moves:
 * subscribing to the clock up in TimeGrid would re-render 24 slots per column,
 * once a second, to shift one line by a pixel a minute.
 */
function NowLine() {
  const now = useNow();

  return (
    <div
      className={styles.now}
      style={{ '--now-at': `${dayFraction(new Date(now)) * 100}%` } as CSSProperties}
      aria-hidden="true"
    />
  );
}

function isWeekend(day: Date): boolean {
  return day.getDay() === 0 || day.getDay() === 6;
}
